import { clsx } from "clsx";
import type { ReactNode } from "react";
import { Text } from "../typography";
import type { TypographyTone, TypographyVariant } from "../typography";

export type ChipVariant =
  | "primary"
  | "secondary"
  | "tertiary"
  | "userBubble"
  | "approved"
  | "rejected"
  | "expired"
  | "inReview"
  | "underReview"
  | "aiSection";

export interface ChipProps {
  variant?: ChipVariant;
  icon?: ReactNode;
  className?: string;
  children: ReactNode;
  onClick?: () => void;
  selected?: boolean;
}

const CONTAINER: Record<ChipVariant, string> = {
  primary: "bg-bgWhite border border-borderDefault px-3 py-2 rounded-xl",
  secondary: "bg-accentCyanTint px-3 py-1.5 rounded-xl",
  tertiary: "bg-bgLight border border-borderDefault px-3 py-1.5 rounded-xl",
  userBubble:
    "bg-bgLight border border-borderDefault px-3 py-1.5 rounded-tl-xl rounded-tr-xl rounded-bl-xl rounded-br-[2px]",
  approved: "bg-accentGreenTint border border-accentGreenTint pl-2 pr-3 py-0.5 rounded-xl",
  rejected: "bg-accentErrorTint pl-2 pr-3 py-1 rounded-xl",
  expired:     "bg-bgMuted border border-bgMuted px-3 py-1 rounded-xl",
  inReview:    "bg-accentYellowTint border border-accentYellowTint pl-2 pr-3 py-0.5 rounded-xl",
  underReview: "bg-accentBlueTint px-3 py-1.5 rounded-xl",
  aiSection:   "bg-accentCyanTint px-3 py-2 rounded-xl self-start text-accentCyan700",
};

const TEXT_VARIANT: Record<ChipVariant, TypographyVariant> = {
  primary: "body",
  secondary: "bodySm",
  tertiary: "bodySm",
  userBubble: "bodySm",
  approved:    "bodyXsBold",
  rejected:    "bodyXsBold",
  expired:     "bodyXsBold",
  inReview:    "bodyXsBold",
  underReview: "bodyXsBold",
  aiSection:   "bodySmBold",
};

const TEXT_TONE: Record<ChipVariant, TypographyTone> = {
  primary: "body",
  secondary: "inherit",
  tertiary: "body",
  userBubble: "dark",
  approved:    "inherit",
  rejected:    "inherit",
  expired:     "inherit",
  inReview:    "inherit",
  underReview: "inherit",
  aiSection:   "inherit",
};

const TEXT_COLOR_CLASS: Partial<Record<ChipVariant, string>> = {
  secondary: "text-accentCyan700",
  approved:    "text-accentGreenDark",
  rejected:    "text-accentError",
  expired:     "text-textMuted",
  inReview:    "text-amber-700",
  underReview: "text-accentBlue",
  aiSection:   "text-accentCyan700",
};

export function Chip({ variant = "primary", icon, className, children, onClick, selected }: ChipProps) {
  const isInteractive = Boolean(onClick);
  const containerClass = clsx(
    "inline-flex items-center gap-1.5",
    CONTAINER[variant],
    TEXT_COLOR_CLASS[variant],
    isInteractive && "cursor-pointer transition-colors",
    isInteractive && selected && "!border-accentError",
    isInteractive && !selected && "hover:!border-borderDark",
    className,
  );

  const inner = (
    <>
      {icon ? <span className="inline-flex size-5 items-center justify-center">{icon}</span> : null}
      <Text variant={TEXT_VARIANT[variant]} tone={TEXT_TONE[variant]} inline>
        {children}
      </Text>
    </>
  );

  if (isInteractive) {
    return <button type="button" onClick={onClick} className={containerClass}>{inner}</button>;
  }
  return <span className={containerClass}>{inner}</span>;
}
